import React from 'react';

import { Button, Grid, Typography } from '@mui/material';
import { isNotNil } from 'es-toolkit';

import { DateFormatPatterns } from '@clearverify/constants';
import { formatDate, getDateTimeInTimezone } from '@clearverify/utils';

import { VerificationRequestStatus } from 'src/api';
import { reportAvailableVerificationRequestStatuses } from 'src/constants/inbox';
import { ModalId } from 'src/constants/modals';
import { VERIFICATION_REQUEST_TYPE_LABEL } from 'src/constants/verificationRequest';
import { useGeneratedReports } from 'src/hooks/generatedReport';
import { useInboxItemParamId } from 'src/hooks/inboxItem';
import { useModal } from 'src/hooks/shared';
import { useCurrentUser } from 'src/hooks/user';
import { useVerificationRequest } from 'src/hooks/verificationRequest';
import { ViewVerificationRequestReportModalArgs } from 'src/modals/verifier';
import { composeSx } from 'src/utils/styles';

import { STATUS_TO_NOTE_MAPPING } from './constants';
import { requestBodyContainerStyles, viewReportButtonStyles } from './shared/styles';

export const InboxItemContentVerificationRequestEmployer: React.FC = () => {
  const { openModal } = useModal();

  const { timezone } = useCurrentUser();

  const inboxItemId = useInboxItemParamId();
  const { verificationRequest } = useVerificationRequest({ inboxItemId });
  const { generatedReportsMeta, loading } = useGeneratedReports({ inboxItemId });

  const isReportAvailable = React.useMemo(
    () =>
      verificationRequest?.status &&
      reportAvailableVerificationRequestStatuses.includes(verificationRequest.status),
    [verificationRequest?.status],
  );

  const reportIsGenerated = React.useMemo(
    () => generatedReportsMeta.some(isNotNil),
    [generatedReportsMeta],
  );

  const generatedReportCreatedAt = React.useMemo(() => {
    const date = generatedReportsMeta[0]?.createdAt || new Date();
    return formatDate(
      getDateTimeInTimezone(date, timezone)?.toISO(),
      DateFormatPatterns.meridiemTimePaddedWithDate,
    );
  }, [generatedReportsMeta, timezone]);

  const handleViewReportClick = React.useCallback(() => {
    openModal(ModalId.ViewVerificationRequestReportModal, {
      verificationRequest,
      generatedReportsMeta,
      inboxItemId,
    } satisfies ViewVerificationRequestReportModalArgs);
  }, [generatedReportsMeta, inboxItemId, openModal, verificationRequest]);

  if (isReportAvailable) {
    return (
      <Grid container sx={requestBodyContainerStyles} width="100%" direction="column">
        <Typography variant="h6" mb={1}>
          {verificationRequest?.type
            ? VERIFICATION_REQUEST_TYPE_LABEL[verificationRequest.type] || 'Verification'
            : 'Verification'}{' '}
          report has been created
        </Typography>
        <Typography variant="body2">
          Please note that report contents are updated as of {generatedReportCreatedAt}. To view
          this report, click the “view report” button below. Upon clicking this button you will be
          able to download or email the report.
        </Typography>
        <Button
          variant="contained"
          disabled={!reportIsGenerated}
          loading={loading}
          sx={composeSx(viewReportButtonStyles, { mt: 2.5, alignSelf: 'flex-end' })}
          onClick={handleViewReportClick}
        >
          View report
        </Button>
      </Grid>
    );
  }

  return (
    <React.Fragment>
      {verificationRequest?.status && (
        <Grid container sx={requestBodyContainerStyles} width="100%" direction="column">
          <Typography variant="h6" mb={1}>
            {getNote(verificationRequest.status).header}
          </Typography>
          <Typography variant="body2">{getNote(verificationRequest.status).text}</Typography>
        </Grid>
      )}
    </React.Fragment>
  );
};

const getNote = (verificationRequestStatus: VerificationRequestStatus) => {
  return STATUS_TO_NOTE_MAPPING[verificationRequestStatus];
};
